import React, { useState, useEffect, useCallback } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Alert,
  Chip,
  Box,
  AppBar,
  Toolbar,
  Grid,
  Card,
  CardContent,
  TablePagination,
  InputAdornment,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Checkbox
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ShoppingCart as OrdersIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Schedule as PendingIcon,
  Phone as PhoneIcon,
  Receipt as ReceiptIcon,
  Image as ImageIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Logout as LogoutIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import adminAuthService from '../services/adminAuthService';
import { API_BASE_URL } from '../config/api';

// City to Province Code Mapping for Prime Delivery
const CITY_TO_PROVINCE_CODE = {
  'بغداد': 'BGD',
  'الناصرية ذي قار': 'NAS',
  'ذي قار': 'NAS',
  'الناصرية': 'NAS',
  'ديالى': 'DYL',
  'الكوت واسط': 'KOT',
  'الكوت': 'KOT',
  'واسط': 'KOT',
  'كربلاء': 'KRB',
  'دهوك': 'DOH',
  'بابل الحلة': 'BBL',
  'بابل': 'BBL',
  'النجف': 'NJF',
  'البصرة': 'BAS',
  'اربيل': 'ARB',
  'أربيل': 'ARB',
  'كركوك': 'KRK',
  'السليمانيه': 'SMH',
  'السليمانية': 'SMH',
  'سليمانية': 'SMH',
  'سليمانيه': 'SMH',
  'صلاح الدين': 'SAH',
  'الانبار': 'ANB',
  'الأنبار': 'ANB',
  'السماوة المثنى': 'SAM',
  'السماوة': 'SAM',
  'المثنى': 'SAM',
  'موصل': 'MOS',
  'الموصل': 'MOS',
  'الديوانية': 'DWN',
  'القادسية': 'DWN',
  'العمارة ميسان': 'AMA',
  'العمارة': 'AMA',
  'ميسان': 'AMA'
};

const PackagingManagement = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentUser = adminAuthService.getCurrentUser();
  const isPackagingAdmin = currentUser?.role === 'packaging_admin';
  
  // RTL theme configuration
  const rtlTheme = createTheme({
    direction: 'rtl',
    typography: {
      fontFamily: '"Cairo", "Arial", sans-serif'
    },
    components: {
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid #e0e7ff',
            overflow: 'hidden'
          }
        }
      },
      MuiTable: {
        styleOverrides: {
          root: {
            minWidth: '100%'
          }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: '#f8fafc',
            '& .MuiTableCell-head': {
              backgroundColor: '#f8fafc',
              fontWeight: 700,
              fontSize: '0.9rem',
              color: '#374151',
              borderBottom: '2px solid #e5e7eb',
              padding: '16px 12px'
            }
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            textAlign: 'right',
            padding: '12px',
            borderBottom: '1px solid #f1f5f9',
            fontSize: '0.875rem'
          },
          head: {
            backgroundColor: '#f8fafc',
            fontWeight: 700,
            fontSize: '0.9rem',
            color: '#374151',
            borderBottom: '2px solid #e5e7eb'
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: '#f8fafc',
              transition: 'background-color 0.2s ease'
            },
            '&:last-child .MuiTableCell-root': {
              borderBottom: 'none'
            }
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-input': {
              textAlign: 'right'
            }
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            direction: 'rtl'
          }
        }
      }
    }
  });
  
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [todayShippedFilter, setTodayShippedFilter] = useState(false);
  const [shippedDateFilter, setShippedDateFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingItems, setEditingItems] = useState(false);
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newProductQuantity, setNewProductQuantity] = useState(1);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Admin Notes state
  const [isEditingAdminNotes, setIsEditingAdminNotes] = useState(false);
  const [adminNotesValue, setAdminNotesValue] = useState('');
  const [savingAdminNotes, setSavingAdminNotes] = useState(false);

  // Prepared items state - track which items have been prepared per order
  const [preparedItemsByOrder, setPreparedItemsByOrder] = useState({});
  const [preparedItems, setPreparedItems] = useState(new Set());

  // CSV Export state
  const [selectedOrdersForExport, setSelectedOrdersForExport] = useState([]);
  const [selectAllOrders, setSelectAllOrders] = useState(false);

  // Missing Products state
  const [missingProducts, setMissingProducts] = useState([]);
  const [missingProductsDialogOpen, setMissingProductsDialogOpen] = useState(false);

  // Statistics state
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0,
    todayShipped: 0
  });

  const statusOptions = [
    { value: 'pending', label: 'في الانتظار', color: 'warning', icon: <PendingIcon /> },
    { value: 'confirmed', label: 'مؤكد', color: 'info', icon: <CheckIcon /> },
    { value: 'shipped', label: 'تم الشحن', color: 'primary', icon: <ShippingIcon /> },
    { value: 'delivered', label: 'تم التوصيل', color: 'success', icon: <CheckIcon /> },
    { value: 'cancelled', label: 'ملغي', color: 'error', icon: <CancelIcon /> }
  ];

  // Helper function for status colors
  const getStatusColor = (colorType) => {
    const colors = {
      warning: '#f59e0b',
      info: '#3b82f6', 
      primary: '#6366f1',
      success: '#10b981',
      error: '#ef4444'
    };
    return colors[colorType] || '#6b7280';
  };

  // WhatsApp function
  const handleWhatsAppClick = (phoneNumber) => {
    if (!phoneNumber) return;
    
    // Clean phone number and format for WhatsApp
    let cleanNumber = phoneNumber.replace(/\D/g, '');
    
    // Add country code if not present (assuming Iraqi numbers)
    if (!cleanNumber.startsWith('964')) {
      if (cleanNumber.startsWith('0')) {
        cleanNumber = '964' + cleanNumber.substring(1);
      } else {
        cleanNumber = '964' + cleanNumber;
      }
    }
    
    const whatsappUrl = `https://wa.me/${cleanNumber}`;
    window.open(whatsappUrl, '_blank');
  };

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Loading orders for packaging...');
      const response = await fetch(`${API_BASE_URL}/api/admin_orders.php`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Orders response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Orders data received:', data);
      
      if (data.success && Array.isArray(data.orders)) {
        // Check first few orders for shipped_at field
        console.log('Sample order data:', data.orders.slice(0, 3));
        const shippedOrders = data.orders.filter(o => o.status === 'shipped');
        console.log('Shipped orders count:', shippedOrders.length);
        console.log('First shipped order:', shippedOrders[0]);
        
        // Filter to show only confirmed and shipped orders for packaging
        const packagingOrders = data.orders.filter(order => 
          order.status === 'confirmed' || order.status === 'shipped'
        );
        setOrders(packagingOrders);
        calculateStats(packagingOrders);
      } else {
        console.error('Invalid orders data structure:', data);
        setError('خطأ في تحميل بيانات الطلبات');
        setOrders([]);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      setError('فشل في تحميل الطلبات: ' + error.message);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (adminAuthService.isAuthenticated()) {
      loadOrders();
    }
  }, [loadOrders]);

  const filterOrders = useCallback(() => {
    let filtered = orders;

    if (searchQuery) {
      filtered = filtered.filter(order =>
        order.id.toString().includes(searchQuery) ||
        (order.order_code && order.order_code.toString().includes(searchQuery)) ||
        (order.customer_name && order.customer_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (order.customer_email && order.customer_email.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (order.customer_phone && order.customer_phone.toString().includes(searchQuery))
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    if (todayShippedFilter) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      console.log('Today Shipped Filter Active');
      console.log('Today range:', today, 'to', tomorrow);
      
      filtered = filtered.filter(order => {
        if (!order.shipped_at || order.status !== 'shipped') {
          if (order.status === 'shipped' && !order.shipped_at) {
            console.log('Order', order.id, 'has status shipped but no shipped_at date');
          }
          return false;
        }
        const shippedDate = new Date(order.shipped_at);
        const isToday = shippedDate >= today && shippedDate < tomorrow;
        console.log('Order', order.id, '- shipped_at:', order.shipped_at, '- Date:', shippedDate, '- Is today:', isToday);
        return isToday;
      });
      
      console.log('Filtered orders count:', filtered.length);
    }

    // Filter by specific shipped date
    if (shippedDateFilter) {
      const selectedDate = new Date(shippedDateFilter);
      selectedDate.setHours(0, 0, 0, 0);
      const nextDay = new Date(selectedDate);
      nextDay.setDate(nextDay.getDate() + 1);
      
      filtered = filtered.filter(order => {
        if (!order.shipped_at || order.status !== 'shipped') {
          return false;
        }
        const shippedDate = new Date(order.shipped_at);
        return shippedDate >= selectedDate && shippedDate < nextDay;
      });
    }

    // Sort by creation date (newest first)
    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    setFilteredOrders(filtered);
  }, [orders, searchQuery, statusFilter, todayShippedFilter, shippedDateFilter]);

  useEffect(() => {
    filterOrders();
  }, [orders, searchQuery, statusFilter, todayShippedFilter, shippedDateFilter, filterOrders]);

  const calculateStats = (ordersList) => {
    // Calculate today's date range
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const stats = {
      total: ordersList.length,
      pending: ordersList.filter(order => order.status === 'pending').length,
      confirmed: ordersList.filter(order => order.status === 'confirmed').length,
      shipped: ordersList.filter(order => order.status === 'shipped').length,
      delivered: ordersList.filter(order => order.status === 'delivered').length,
      cancelled: ordersList.filter(order => order.status === 'cancelled').length,
      todayShipped: ordersList.filter(order => {
        if (!order.shipped_at || order.status !== 'shipped') return false;
        const shippedDate = new Date(order.shipped_at);
        return shippedDate >= today && shippedDate < tomorrow;
      }).length
    };
    setStats(stats);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusInfo = (status) => {
    return statusOptions.find(s => s.value === status) || statusOptions[0];
  };

  // Handle status change
  const handleStatusChange = async (orderId, newStatusValue) => {
    try {
      console.log(`Changing order ${orderId} status to ${newStatusValue}`);
      
      // Prepare the request body
      const requestBody = {
        action: 'update_status',
        order_id: orderId,
        status: newStatusValue
      };

      // If status is being changed to "shipped", include the current timestamp
      if (newStatusValue === 'shipped') {
        requestBody.shipped_at = new Date().toISOString();
      }
      
      const response = await fetch(`${API_BASE_URL}/api/admin_orders.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      console.log('Status change response:', data);

      if (data.success) {
        // Update local state
        const updatedOrders = orders.map(order => {
          if (order.id === orderId) {
            const updatedOrder = { ...order, status: newStatusValue };
            // Add shipped_at timestamp if status is shipped
            if (newStatusValue === 'shipped') {
              updatedOrder.shipped_at = new Date().toISOString();
            }
            return updatedOrder;
          }
          return order;
        });
        setOrders(updatedOrders);
        calculateStats(updatedOrders);
        
        // Update selectedOrder if it's the one being changed
        if (selectedOrder && selectedOrder.id === orderId) {
          const updatedSelectedOrder = { ...selectedOrder, status: newStatusValue };
          if (newStatusValue === 'shipped') {
            updatedSelectedOrder.shipped_at = new Date().toISOString();
          }
          setSelectedOrder(updatedSelectedOrder);
        }
        
        setSuccess(`تم تحديث حالة الطلب #${orderId} بنجاح إلى "${statusOptions.find(s => s.value === newStatusValue)?.label}"`);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'فشل تحديث حالة الطلب');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      setError('حدث خطأ أثناء تحديث حالة الطلب: ' + error.message);
    }
  };

  // Load available products for adding to order
  const loadAvailableProducts = async (searchQuery = '') => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setAvailableProducts([]);
      return;
    }

    try {
      setLoadingProducts(true);
      console.log('Searching products:', searchQuery);
      
      const response = await fetch(`${API_BASE_URL}/api/admin_products.php?search=${encodeURIComponent(searchQuery)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json',
        }
      });

      console.log('Products API response:', response.status);
      const data = await response.json();
      console.log('Products data:', data);
      
      if (response.ok && data.success) {
        setAvailableProducts(data.products || []);
        console.log('Loaded products:', data.products?.length || 0);
      } else {
        console.error('Failed to load products:', data.message);
        setError(data.message || 'فشل في تحميل المنتجات');
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setError('حدث خطأ في تحميل المنتجات');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Debounce search
  useEffect(() => {
    if (!addProductDialogOpen) return;

    const timeoutId = setTimeout(() => {
      loadAvailableProducts(productSearchQuery);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [productSearchQuery, addProductDialogOpen]);

  // Load missing products on mount
  useEffect(() => {
    loadMissingProducts();
  }, []);

  const handleOpenAddProductDialog = () => {
    setAddProductDialogOpen(true);
    setSelectedProduct(null);
    setNewProductQuantity(1);
    setProductSearchQuery('');
    setAvailableProducts([]); // Clear products when opening
  };

  // Function to round delivery price DOWN to nearest 250 increment
  // Examples: 5001-5249 → 5000, 5250-5499 → 5250, 5500-5749 → 5500, 5750-5999 → 5750
  const roundDeliveryPrice = (price) => {
    return Math.floor(price / 250) * 250;
  };

  // Function to recalculate order totals based on items with weight-based delivery
  const recalculateOrderTotals = (items) => {
    if (!items || items.length === 0) {
      return {
        subtotal: 0,
        delivery: 5000, // Minimum delivery
        total: 5000
      };
    }

    // Calculate subtotal and total weight
    let subtotal = 0;
    let totalWeight = 0;

    items.forEach(item => {
      const quantity = parseFloat(item.quantity || 0);
      const price = parseFloat(item.price || 0);
      const weight = parseFloat(item.weight || 0);
      
      subtotal += quantity * price;
      totalWeight += weight * quantity;
    });

    // Calculate delivery cost based on weight
    // Minimum delivery cost is 5000 IQD
    let delivery = 5000;
    
    // If weight exceeds 5kg, add 1000 IQD per additional kg
    if (totalWeight > 5) {
      const additionalWeight = totalWeight - 5;
      delivery += additionalWeight * 1000;
    }

    // Round delivery price to nearest 250 increment
    delivery = roundDeliveryPrice(delivery);

    const total = subtotal + delivery;

    return {
      subtotal,
      delivery,
      total
    };
  };

  const handleAddProductToOrder = () => {
    if (!selectedProduct || !selectedOrder) return;

    const finalPrice = selectedProduct.after_discount || selectedProduct.sell_price || selectedProduct.price || 0;

    const newItem = {
      id: `new_${Date.now()}`,
      order_item_id: null,
      is_new: true,
      product_id: selectedProduct.id,
      product_name: selectedProduct.name,
      product_sku: selectedProduct.sku || '',
      brand_name: selectedProduct.brand_name || '',
      quantity: newProductQuantity,
      price: finalPrice,
      weight: selectedProduct.weight || 0, // Include weight from product
      product_image: selectedProduct.image_url || selectedProduct.image || ''
    };

    const updatedItems = [...(selectedOrder.items || []), newItem];
    
    // Recalculate totals with weight-based delivery
    const totals = recalculateOrderTotals(updatedItems);
    
    // Preserve discount from database
    const discountAmount = parseFloat(selectedOrder.discount_amount || 0);
    
    setSelectedOrder({
      ...selectedOrder,
      items: updatedItems,
      subtotal: totals.subtotal,
      delivery_fee: totals.delivery,
      discount_amount: discountAmount,
      total_amount: totals.subtotal + totals.delivery - discountAmount
    });
    
    setAddProductDialogOpen(false);
    setSuccess(`تم إضافة ${selectedProduct.name} إلى الطلب`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const getFilteredProducts = () => {
    if (!productSearchQuery) return availableProducts;
    
    return availableProducts.filter(product =>
      product.name?.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
      (product.sku && product.sku.toLowerCase().includes(productSearchQuery.toLowerCase())) ||
      (product.barcode && product.barcode.toLowerCase().includes(productSearchQuery.toLowerCase())) ||
      (product.brand_name && product.brand_name.toLowerCase().includes(productSearchQuery.toLowerCase()))
    );
  };

  // Handle item quantity change
  const handleQuantityChange = (itemId, delta) => {
    setSelectedOrder(prev => {
      const updatedItems = prev.items.map(item => {
        if (item.id === itemId) {
          const newQuantity = Math.max(1, parseInt(item.quantity) + delta);
          return { ...item, quantity: newQuantity };
        }
        return item;
      });
      
      // Recalculate totals with weight-based delivery
      const totals = recalculateOrderTotals(updatedItems);
      
      // Preserve discount from database
      const discountAmount = parseFloat(prev.discount_amount || 0);
      
      return {
        ...prev,
        items: updatedItems,
        subtotal: totals.subtotal,
        delivery_fee: totals.delivery,
        discount_amount: discountAmount,
        total_amount: totals.subtotal + totals.delivery - discountAmount
      };
    });
  };

  // Handle item deletion
  const handleDeleteItem = (itemId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) return;
    
    setSelectedOrder(prev => {
      const updatedItems = prev.items.filter(item => item.id !== itemId);
      
      // Recalculate totals with weight-based delivery
      const totals = recalculateOrderTotals(updatedItems);
      
      // Preserve discount from database
      const discountAmount = parseFloat(prev.discount_amount || 0);
      
      return {
        ...prev,
        items: updatedItems,
        subtotal: totals.subtotal,
        delivery_fee: totals.delivery,
        discount_amount: discountAmount,
        total_amount: totals.subtotal + totals.delivery - discountAmount
      };
    });
  };

  // Handle save order changes
  const handleSaveOrderChanges = async () => {
    try {
      // Prepare items data
      const itemsData = selectedOrder.items.map(item => ({
        order_item_id: item.order_item_id || item.id,
        product_id: item.product_id,
        product_name: item.product_name,
        product_sku: item.product_sku || '',
        brand_name: item.brand_name || '',
        quantity: parseInt(item.quantity) || 0,
        price: parseFloat(item.price) || 0
      }));

      const orderData = {
        action: 'update_order',
        order_id: selectedOrder.id,
        customer_name: selectedOrder.customer_name,
        customer_phone: selectedOrder.customer_phone,
        customer_email: selectedOrder.customer_email,
        shipping_address: selectedOrder.shipping_address,
        city: selectedOrder.city,
        status: selectedOrder.status,
        notes: selectedOrder.notes,
        total_amount: selectedOrder.total_amount,
        delivery: selectedOrder.delivery_fee,
        items: itemsData,
        update_items: true,
        recalculate_totals: true,
        items_changed: true
      };

      console.log('Saving order changes:', orderData);

      const response = await fetch(`${API_BASE_URL}/api/admin_orders.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);
      
      if (data.success) {
        setSuccess('تم حفظ التغييرات بنجاح');
        setTimeout(() => setSuccess(''), 3000);
        setEditingItems(false);
        
        // Reload order details
        await handleViewOrderDetails(selectedOrder);
        loadOrders();
      } else {
        setError(data.message || 'فشل في حفظ التغييرات');
      }
    } catch (error) {
      console.error('Error saving order changes:', error);
      setError('حدث خطأ أثناء حفظ التغييرات: ' + error.message);
    }
  };

  // Function to save admin notes
  const handleSaveAdminNotes = async () => {
    if (!selectedOrder) return;
    
    setSavingAdminNotes(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin_orders.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'update_order',
          order_id: selectedOrder.id,
          admin_notes: adminNotesValue
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess('تم حفظ الملاحظات بنجاح');
        
        // Update selected order
        setSelectedOrder(prev => ({
          ...prev,
          admin_notes: adminNotesValue
        }));
        
        // Update orders list
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === selectedOrder.id 
              ? { ...order, admin_notes: adminNotesValue }
              : order
          )
        );
        
        setIsEditingAdminNotes(false);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'فشل في حفظ الملاحظات');
      }
    } catch (error) {
      console.error('Error saving admin notes:', error);
      setError('فشل في حفظ الملاحظات: ' + error.message);
    } finally {
      setSavingAdminNotes(false);
    }
  };

  // Handle toggling item prepared status
  const handleToggleItemPrepared = (itemId) => {
    setPreparedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      
      // Save to persistent storage by order ID
      if (selectedOrder) {
        setPreparedItemsByOrder(prevOrders => ({
          ...prevOrders,
          [selectedOrder.id]: newSet
        }));
      }
      
      return newSet;
    });
  };

  // Load missing products from database
  const loadMissingProducts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/missing_products.php`, {
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`
        }
      });
      const data = await response.json();
      if (data.success) {
        // Convert database format to local format
        const formattedProducts = data.data.map(p => ({
          id: p.id,
          orderId: p.order_id,
          productId: p.product_id,
          productName: p.product_name,
          sku: p.sku || '',
          brandName: p.brand_name || '',
          image: p.image || '',
          quantity: p.quantity,
          addedAt: p.added_at
        }));
        setMissingProducts(formattedProducts);
      }
    } catch (error) {
      console.error('Error loading missing products:', error);
    }
  };

  // Handle marking product as missing
  const handleMarkProductAsMissing = async (product, orderId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/missing_products.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          orderId: orderId,
          productId: product.product_id || product.id,
          productName: product.product_name,
          sku: product.product_sku || '',
          brandName: product.brand_name || '',
          image: product.product_image || '',
          quantity: product.quantity,
          addedAt: new Date().toISOString()
        })
      });

      const data = await response.json();
      if (data.success) {
        loadMissingProducts(); // Reload the list
        setSuccess(data.message);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'فشل في إضافة المنتج');
        setTimeout(() => setError(''), 3000);
      }
    } catch (error) {
      console.error('Error marking product as missing:', error);
      setError('حدث خطأ في إضافة المنتج');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Handle removing product from missing list
  const handleRemoveMissingProduct = async (missingProductId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/missing_products.php`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: missingProductId })
      });

      const data = await response.json();
      if (data.success) {
        loadMissingProducts(); // Reload the list
        setSuccess(data.message);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'فشل في حذف المنتج');
        setTimeout(() => setError(''), 3000);
      }
    } catch (error) {
      console.error('Error removing missing product:', error);
      setError('حدث خطأ في حذف المنتج');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Clear all missing products
  const handleClearMissingProducts = async () => {
    if (window.confirm('هل أنت متأكد من حذف جميع المنتجات المفقودة؟')) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/missing_products.php`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${adminAuthService.getToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ action: 'clear_all' })
        });

        const data = await response.json();
        if (data.success) {
          setMissingProducts([]);
          setSuccess(data.message);
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError(data.message || 'فشل في مسح القائمة');
          setTimeout(() => setError(''), 3000);
        }
      } catch (error) {
        console.error('Error clearing missing products:', error);
        setError('حدث خطأ في مسح القائمة');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  // Export missing products to Excel
  const exportMissingProductsToExcel = () => {
    if (missingProducts.length === 0) {
      setError('لا توجد منتجات مفقودة للتصدير');
      return;
    }

    const rows = missingProducts.map((product, index) => ({
      '#': index + 1,
      'اسم المنتج': product.productName,
      'SKU': product.sku || '-',
      'العلامة التجارية': product.brandName || '-',
      'الكمية': product.quantity,
      'رقم الطلب': product.orderId,
      'تاريخ الإضافة': new Date(product.addedAt).toLocaleDateString('ar-EG')
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const columnWidths = [
      { wch: 5 },  // #
      { wch: 30 }, // اسم المنتج
      { wch: 15 }, // SKU
      { wch: 20 }, // العلامة التجارية
      { wch: 10 }, // الكمية
      { wch: 12 }, // رقم الطلب
      { wch: 15 }  // تاريخ الإضافة
    ];
    worksheet['!cols'] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Missing Products');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `missing_products_${new Date().toISOString().slice(0, 10)}.xlsx`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSuccess(`تم تصدير ${missingProducts.length} منتج مفقود`);
  };

  // Handle order selection for export
  const handleSelectOrderForExport = (orderId) => {
    if (selectedOrdersForExport.includes(orderId)) {
      setSelectedOrdersForExport(selectedOrdersForExport.filter(id => id !== orderId));
    } else {
      setSelectedOrdersForExport([...selectedOrdersForExport, orderId]);
    }
  };

  const handleSelectAllOrdersForExport = () => {
    if (selectAllOrders) {
      setSelectedOrdersForExport([]);
      setSelectAllOrders(false);
    } else {
      const currentPageOrderIds = filteredOrders
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map(order => order.id);
      setSelectedOrdersForExport(currentPageOrderIds);
      setSelectAllOrders(true);
    }
  };

  // Generate CSV for delivery company
  const generateDeliveryCSV = async () => {
    if (selectedOrdersForExport.length === 0) {
      setError('الرجاء تحديد طلبات للتصدير');
      return;
    }

    try {
      setLoading(true);
      
      // Fetch full details for all selected orders
      const orderDetailsPromises = selectedOrdersForExport.map(async (orderId) => {
        const response = await fetch(`${API_BASE_URL}/api/admin_orders.php?order_id=${orderId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${adminAuthService.getToken()}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        return data.success ? data.order : null;
      });

      const ordersWithDetails = await Promise.all(orderDetailsPromises);
      const validOrders = ordersWithDetails.filter(order => order !== null);

      // Generate Excel rows with proper data
      const rows = validOrders.map(order => {
        const totalQuantity = order.items?.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0) || 0;
        
        // Calculate total price: products + delivery - discounts
        const subtotal = order.items?.reduce((sum, item) => {
          return sum + (parseFloat(item.price || 0) * parseFloat(item.quantity || 0));
        }, 0) || parseFloat(order.subtotal || 0) || 0;
        
        const deliveryFee = parseFloat(order.delivery || order.delivery_fee || 5000);
        const discountAmount = parseFloat(order.discount_amount || 0);
        const totalPrice = subtotal + deliveryFee - discountAmount;
        
        const exportDate = new Date().toLocaleDateString('ar-EG');
        
        // Order size is always normal
        const orderSize = 'عادي';

        return {
          'اسم الزبون': order.order_id || order.id || '',
          'رقم الهاتف الاساسي': order.customer_phone || '',
          'رقم الهاتف الثانوي': '', // Secondary phone (not available)
          'المحافظة': order.city || '',
          'المنطقة': order.shipping_address || '',
          'نوع البضاعه': 'عدد يدوية', // Product type
          'عدد القطع': totalQuantity,
          'السعر مع التوصيل': totalPrice,
          'حجم الطلب': orderSize,
          'الملاحظات': `تاريخ التصدير: ${exportDate}${order.admin_notes ? ' - ' + order.admin_notes : ''}`,
          'نوع الطلب': 'طلب جديد' // Order type
        };
      });

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(rows);
      
      // Set column widths for better readability
      const columnWidths = [
        { wch: 20 }, // اسم الزبون
        { wch: 15 }, // رقم الهاتف الاساسي
        { wch: 15 }, // رقم الهاتف الثانوي
        { wch: 15 }, // المحافظة
        { wch: 30 }, // المنطقة
        { wch: 15 }, // نوع البضاعه
        { wch: 12 }, // عدد القطع
        { wch: 15 }, // السعر مع التوصيل
        { wch: 12 }, // حجم الطلب
        { wch: 30 }, // الملاحظات
        { wch: 15 }  // نوع الطلب
      ];
      worksheet['!cols'] = columnWidths;

      // Set RTL for the worksheet
      if (!worksheet['!cols']) worksheet['!cols'] = [];
      worksheet['!dir'] = 'rtl';

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Delivery Orders');

      // Generate Excel file and download
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `delivery_orders_${new Date().toISOString().slice(0, 10)}.xlsx`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess(`تم تصدير ${validOrders.length} طلب`);
      setSelectedOrdersForExport([]);
      setSelectAllOrders(false);
      setLoading(false);
    } catch (error) {
      console.error('Error generating CSV:', error);
      setError('حدث خطأ أثناء تصدير الطلبات');
      setLoading(false);
    }
  };

  // Generate Stock Update Excel for delivery company
  const generateStockExport = async () => {
    if (selectedOrdersForExport.length === 0) {
      setError('الرجاء تحديد طلبات للتصدير');
      return;
    }

    try {
      setLoading(true);
      
      // Fetch full details for all selected orders
      const orderDetailsPromises = selectedOrdersForExport.map(async (orderId) => {
        const response = await fetch(`${API_BASE_URL}/api/admin_orders.php?order_id=${orderId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${adminAuthService.getToken()}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        return data.success ? data.order : null;
      });

      const ordersWithDetails = await Promise.all(orderDetailsPromises);
      const validOrders = ordersWithDetails.filter(order => order !== null);

      // Aggregate all items from selected orders
      const allItems = [];
      const exportDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

      validOrders.forEach(order => {
        if (order.items && Array.isArray(order.items)) {
          order.items.forEach(item => {
            allItems.push({
              'Invoice No.': '',
              'Customer name': 'online',
              'Customer Phone number': '7813438118',
              'Customer Email': 'alatharibaq@gmail.com',
              'Sale Date': exportDate,
              'Product name': item.product_name || '',
              'Product SKU': item.product_sku || '',
              'Quantity': parseInt(item.quantity) || 0,
              'Product Unit': 'قطعة',
              'Unit Price': parseFloat(item.purchase_price || 0),
              'Item Tax': '',
              'Item Discount': '',
              'Item Description': '',
              'Types of service': '',
              'Custom Field 1': '',
              'Custom Field 2': '',
              'Custom Field 3': '',
              'Custom Field 4': ''
            });
          });
        }
      });

      if (allItems.length === 0) {
        setError('لا توجد منتجات في الطلبات المحددة');
        setLoading(false);
        return;
      }

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(allItems);
      
      // Set column widths
      const columnWidths = [
        { wch: 12 }, // Invoice No.
        { wch: 15 }, // Customer name
        { wch: 18 }, // Customer Phone number
        { wch: 25 }, // Customer Email
        { wch: 12 }, // Sale Date
        { wch: 30 }, // Product name
        { wch: 15 }, // Product SKU
        { wch: 10 }, // Quantity
        { wch: 12 }, // Product Unit
        { wch: 12 }, // Unit Price
        { wch: 12 }, // Item Tax
        { wch: 12 }, // Item Discount
        { wch: 20 }, // Item Description
        { wch: 15 }, // Types of service
        { wch: 15 }, // Custom Field 1
        { wch: 15 }, // Custom Field 2
        { wch: 15 }, // Custom Field 3
        { wch: 15 }  // Custom Field 4
      ];
      worksheet['!cols'] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock Update');

      // Generate Excel file and download
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `stock_update_${new Date().toISOString().slice(0, 10)}.xlsx`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess(`تم تصدير ${allItems.length} منتج من ${validOrders.length} طلب`);
      setSelectedOrdersForExport([]);
      setSelectAllOrders(false);
      setLoading(false);
    } catch (error) {
      console.error('Error generating stock export:', error);
      setError('حدث خطأ أثناء تصدير المخزون');
      setLoading(false);
    }
  };

  // Helper function to aggregate products from selected orders
  const aggregateProductsFromOrders = async () => {
    try {
      if (selectedOrdersForExport.length === 0) {
        setError('الرجاء تحديد طلبات لتصدير قائمة المنتجات');
        return null;
      }

      // Fetch detailed order data for all selected orders
      const orderDetailsPromises = selectedOrdersForExport.map(orderId =>
        fetch(`${API_BASE_URL}/api/admin_orders.php?order_id=${orderId}`, {
          headers: {
            'Authorization': `Bearer ${adminAuthService.getToken()}`
          }
        }).then(res => res.json())
      );

      const orderResults = await Promise.all(orderDetailsPromises);
      
      // Aggregate products by SKU
      const productsMap = new Map();
      
      orderResults.forEach(result => {
        if (result.success && result.order && result.order.items) {
          result.order.items.forEach(item => {
            const key = item.sku || item.product_sku || item.product_id;
            if (productsMap.has(key)) {
              const existing = productsMap.get(key);
              existing.quantity += parseInt(item.quantity) || 0;
            } else {
              // Get image URL and convert to absolute if needed
              let imageUrl = item.product_image || item.image_url || item.image;
              if (imageUrl && !imageUrl.startsWith('http')) {
                imageUrl = `${API_BASE_URL}/api/uploads/products/${imageUrl}`;
              }
              
              productsMap.set(key, {
                name: item.product_name,
                sku: item.sku || item.product_sku,
                quantity: parseInt(item.quantity) || 0,
                image: imageUrl,
                product_id: item.product_id
              });
            }
          });
        }
      });

      return Array.from(productsMap.values());
    } catch (error) {
      console.error('Error aggregating products:', error);
      setError('حدث خطأ في تجميع قائمة المنتجات: ' + error.message);
      return null;
    }
  };

  // Function to generate products list export
  const generateProductsListExport = async () => {
    const products = await aggregateProductsFromOrders();
    
    if (!products || products.length === 0) {
      setError('لا توجد منتجات للتصدير');
      return;
    }

    try {
      setLoading(true);
      
      // Create HTML content for export
      const htmlContent = `
        <!DOCTYPE html>
        <html dir="rtl" lang="ar">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>قائمة المنتجات</title>
          <style>
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            body {
              font-family: 'Cairo', Arial, sans-serif;
              direction: rtl;
              padding: 10px;
              background: white;
            }
            h1 {
              text-align: center;
              color: #1976d2;
              margin-bottom: 20px;
              font-size: 24px;
            }
            .products-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 10px;
              margin-top: 15px;
            }
            .product-card {
              border: 2px solid #e0e0e0;
              border-radius: 8px;
              padding: 10px;
              text-align: center;
              background: white;
              display: flex;
              flex-direction: column;
              align-items: center;
              transition: all 0.3s ease;
              position: relative;
            }
            .product-card.checked {
              background: #d4edda;
              border-color: #10b981;
            }
            .checkbox-container {
              position: absolute;
              top: 10px;
              left: 10px;
              z-index: 10;
            }
            .custom-checkbox {
              width: 30px;
              height: 30px;
              border: 2px solid #6b7280;
              border-radius: 6px;
              background: #f3f4f6;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.3s ease;
            }
            .custom-checkbox:hover {
              transform: scale(1.1);
              box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            }
            .custom-checkbox.checked {
              background: #10b981;
              border-color: #10b981;
            }
            .custom-checkbox.checked::after {
              content: '✓';
              color: white;
              font-size: 20px;
              font-weight: bold;
            }
            .product-image {
              width: 100%;
              max-width: 120px;
              height: 120px;
              object-fit: cover;
              border-radius: 4px;
              margin-bottom: 8px;
              margin-top: 20px;
            }
            .product-name {
              font-weight: bold;
              font-size: 13px;
              margin: 5px 0;
              color: #333;
              line-height: 1.3;
              word-break: break-word;
            }
            .product-sku {
              color: #666;
              font-size: 11px;
              margin: 3px 0;
            }
            .product-quantity {
              background: #4caf50;
              color: white;
              padding: 6px 12px;
              border-radius: 15px;
              font-weight: bold;
              display: inline-block;
              margin-top: 8px;
              font-size: 13px;
            }
            .summary {
              text-align: center;
              margin-bottom: 15px;
              padding: 12px;
              background: #f5f5f5;
              border-radius: 8px;
              font-size: 13px;
            }
            .summary strong {
              color: #1976d2;
            }
            .summary .checked-count {
              color: #10b981;
              font-weight: bold;
              margin-right: 10px;
            }
            
            /* Print/Export buttons */
            .export-buttons {
              text-align: center;
              margin-bottom: 20px;
              display: flex;
              gap: 10px;
              justify-content: center;
              flex-wrap: wrap;
            }
            .export-btn {
              background: #1976d2;
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 8px;
              font-size: 16px;
              font-weight: bold;
              cursor: pointer;
              font-family: 'Cairo', Arial, sans-serif;
              transition: background 0.3s ease;
              display: inline-flex;
              align-items: center;
              gap: 8px;
            }
            .export-btn:hover {
              background: #1565c0;
            }
            .export-btn.secondary {
              background: #4caf50;
            }
            .export-btn.secondary:hover {
              background: #45a049;
            }
            
            @media print {
              .export-buttons {
                display: none;
              }
              .checkbox-container {
                display: none;
              }
            }
            
            /* Tablet and larger screens */
            @media (min-width: 600px) {
              body { padding: 20px; }
              h1 { font-size: 28px; margin-bottom: 30px; }
              .products-grid {
                grid-template-columns: repeat(3, 1fr);
                gap: 15px;
              }
              .product-card { padding: 15px; }
              .product-image {
                max-width: 140px;
                height: 140px;
              }
              .product-name { font-size: 15px; }
              .product-sku { font-size: 13px; }
              .product-quantity { font-size: 14px; }
              .summary { font-size: 15px; }
            }
            
            /* Desktop and print */
            @media (min-width: 900px) {
              .products-grid {
                grid-template-columns: repeat(4, 1fr);
                gap: 20px;
              }
              .product-image {
                max-width: 150px;
                height: 150px;
              }
              .product-name { font-size: 16px; }
              .product-sku { font-size: 14px; }
            }
            
            @media print {
              body { padding: 10px; }
              .products-grid { 
                grid-template-columns: repeat(4, 1fr);
                gap: 15px;
              }
              .product-card { 
                page-break-inside: avoid;
                border: 1px solid #ccc;
              }
            }
          </style>
        </head>
        <body>
          <h1>قائمة المنتجات - الطلبات المحددة</h1>
          
          <div class="export-buttons">
            <button class="export-btn" onclick="window.print()">
              🖨️ طباعة / حفظ PDF
            </button>
            <button class="export-btn secondary" onclick="downloadAsHTML()">
              💾 حفظ كملف HTML
            </button>
          </div>
          
          <div class="summary">
            <strong>عدد الطلبات:</strong> ${selectedOrdersForExport.length} | 
            <strong>إجمالي المنتجات:</strong> ${products.length} | 
            <strong>التاريخ:</strong> ${new Date().toLocaleDateString('ar-IQ')} |
            <span class="checked-count">تم التجهيز: <span id="checkedCount">0</span> / ${products.length}</span>
          </div>
          <div class="products-grid">
            ${products.map((product, index) => `
              <div class="product-card" data-index="${index}">
                <div class="checkbox-container">
                  <div class="custom-checkbox" onclick="toggleCheck(${index})"></div>
                </div>
                ${product.image ? `<img src="${product.image}" alt="${product.name}" class="product-image" onerror="this.src='https://via.placeholder.com/150?text=No+Image'">` : '<div class="product-image" style="background: #f0f0f0; display: flex; align-items: center; justify-content: center; margin-top: 20px;">لا توجد صورة</div>'}
                <div class="product-name">${product.name}</div>
                <div class="product-sku">SKU: ${product.sku || 'غير متوفر'}</div>
                <div class="product-quantity">الكمية: ${product.quantity}</div>
              </div>
            `).join('')}
          </div>
          
          <script>
            let checkedItems = new Set();
            
            function toggleCheck(index) {
              const card = document.querySelector(\`.product-card[data-index="\${index}"]\`);
              const checkbox = card.querySelector('.custom-checkbox');
              
              if (checkedItems.has(index)) {
                checkedItems.delete(index);
                card.classList.remove('checked');
                checkbox.classList.remove('checked');
              } else {
                checkedItems.add(index);
                card.classList.add('checked');
                checkbox.classList.add('checked');
              }
              
              updateCheckedCount();
            }
            
            function updateCheckedCount() {
              document.getElementById('checkedCount').textContent = checkedItems.size;
            }
            
            function downloadAsHTML() {
              const blob = new Blob([document.documentElement.outerHTML], { type: 'text/html' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'products_list_${new Date().toISOString().slice(0, 10)}.html';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            }
          </script>
        </body>
        </html>
      `;

      // Open in new window for printing/saving
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      setSuccess(`تم تجهيز قائمة ${products.length} منتج من ${selectedOrdersForExport.length} طلب`);
      
    } catch (error) {
      console.error('Error generating products list:', error);
      setError('حدث خطأ في إنشاء قائمة المنتجات: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate Prime Delivery Export
  const generatePrimeDeliveryExport = async () => {
    try {
      if (selectedOrdersForExport.length === 0) {
        setError('الرجاء تحديد طلبات للتصدير');
        return;
      }

      setLoading(true);

      // Fetch full order details for all selected orders
      const orderDetailsPromises = selectedOrdersForExport.map(async (orderId) => {
        const response = await fetch(`${API_BASE_URL}/api/admin_orders.php?order_id=${orderId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${adminAuthService.getToken()}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        return data.success ? data.order : null;
      });

      const ordersWithDetails = await Promise.all(orderDetailsPromises);
      const validOrders = ordersWithDetails.filter(order => order !== null);

      if (validOrders.length === 0) {
        setError('لم يتم العثور على طلبات صالحة');
        setLoading(false);
        return;
      }

      // Track unmapped cities
      const unmappedCities = new Set();

      // Format phone number: remove leading 0 or +964, keep 10 digits
      const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        let cleaned = phone.toString().replace(/\s+/g, ''); // Remove spaces
        // Remove +964 country code
        if (cleaned.startsWith('+964')) {
          cleaned = cleaned.substring(4);
        } else if (cleaned.startsWith('964')) {
          cleaned = cleaned.substring(3);
        }
        // Remove leading 0
        if (cleaned.startsWith('0')) {
          cleaned = cleaned.substring(1);
        }
        return cleaned;
      };

      // Get province code from city
      const getProvinceCode = (city) => {
        if (!city) return '';
        const code = CITY_TO_PROVINCE_CODE[city];
        if (!code) {
          unmappedCities.add(city);
          return city; // Return original city name if not mapped
        }
        return code;
      };

      // Calculate total items count
      const getTotalItemsCount = (order) => {
        if (!order.items || !Array.isArray(order.items)) return 0;
        return order.items.reduce((total, item) => total + parseInt(item.quantity || 0), 0);
      };

      // Calculate total price (including delivery and after discount)
      const getTotalPrice = (order) => {
        // Calculate subtotal from items (matching existing delivery export logic)
        const subtotal = order.items?.reduce((sum, item) => {
          return sum + (parseFloat(item.price || 0) * parseFloat(item.quantity || 0));
        }, 0) || parseFloat(order.subtotal || 0) || 0;
        
        const deliveryFee = parseFloat(order.delivery || order.delivery_fee || 5000);
        const discountAmount = parseFloat(order.discount_amount || 0);
        const totalPrice = subtotal + deliveryFee - discountAmount;
        
        return Math.round(totalPrice); // Round to nearest whole number
      };

      // Get today's date in YYYY-MM-DD format
      const exportDate = new Date().toISOString().slice(0, 10);
      
      // Fixed note for electrical products inspection
      const fixedNote = 'يتم فحص جميع المنتجات الكهربائية بحضور المندوب حصراً  ويكون الفحص كهربائي  للتشغيل لاغير دون  استعمال فعلي';

      // Transform data to Prime delivery format
      const rows = validOrders.map(order => ({
        'ملاحظات': `${exportDate} - ${fixedNote}`,
        'عدد القطع': getTotalItemsCount(order),
        'يحتوي على ارجاع بضاعة؟': '',
        'هاتف المستلم': formatPhoneNumber(order.customer_phone),
        'تفاصيل العنوان': order.shipping_address || order.address || '',
        'شفرة المحافظة': getProvinceCode(order.city),
        'أسم المستلم': order.order_id || order.id || '',
        'المبلغ عراقي': getTotalPrice(order),
        'رقم الوصل': '',
        'كود الشحنة': '',
        'هاتف المستلم 2': '',
        'نوع البضاعة': 'معدات يدوية',
        'وصف البضاعة المسترجعة اوالمستبدلة': ''
      }));

      // Create worksheet from JSON
      const worksheet = XLSX.utils.json_to_sheet(rows);

      // Set column widths for better readability
      worksheet['!cols'] = [
        { wch: 80 },  // ملاحظات (increased for date + fixed note)
        { wch: 10 },  // عدد القطع
        { wch: 20 },  // يحتوي على ارجاع بضاعة؟
        { wch: 15 },  // هاتف المستلم
        { wch: 30 },  // تفاصيل العنوان
        { wch: 15 },  // شفرة المحافظة
        { wch: 15 },  // أسم المستلم
        { wch: 15 },  // المبلغ عراقي
        { wch: 15 },  // رقم الوصل
        { wch: 15 },  // كود الشحنة
        { wch: 15 },  // هاتف المستلم 2
        { wch: 15 },  // نوع البضاعة
        { wch: 30 }   // وصف البضاعة المسترجعة اوالمستبدلة
      ];

      // Create workbook and add worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Prime Delivery');

      // Generate and download XLSX file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `prime_delivery_${exportDate}.xlsx`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Show success message
      let successMessage = `تم تصدير ${validOrders.length} طلب إلى Prime Delivery`;
      
      // Warn about unmapped cities if any
      if (unmappedCities.size > 0) {
        const citiesList = Array.from(unmappedCities).join('، ');
        successMessage += ` - تحذير: المدن التالية غير مدرجة في قائمة المحافظات: ${citiesList}`;
      }

      setSuccess(successMessage);
      setSelectedOrdersForExport([]);
      setSelectAllOrders(false);
      setLoading(false);

    } catch (error) {
      console.error('Error generating Prime delivery export:', error);
      setError('حدث خطأ أثناء تصدير طلبات Prime Delivery: ' + error.message);
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    adminAuthService.logout();
    navigate('/admin/login');
  };

  // Handle clear cache and refresh
  const handleClearCacheAndRefresh = () => {
    // Clear all caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }
    
    // Force reload from server, bypassing cache
    window.location.reload(true);
  };

  // Handle view order details
  const handleViewOrderDetails = async (order) => {
    try {
      console.log('Requesting details for order:', order.id);
      
      const apiUrl = `${API_BASE_URL}/api/admin_orders.php?order_id=${order.id}&_t=${Date.now()}`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      console.log('Order details data:', data);
      
      if (response.ok && data.success) {
        const orderData = data.order;
        
        // Normalize items structure
        if (orderData.items) {
          orderData.items = orderData.items.map(item => ({
            ...item,
            id: item.order_item_id,
            order_item_id: item.order_item_id
          }));
        }
        
        // Preserve discount information from backend
        orderData.discount_amount = parseFloat(orderData.discount_amount || 0);
        orderData.coupon_code = orderData.coupon_code || '';
        
        // Preserve delivery price from database on initial load
        // When items are edited, the delivery will be recalculated using the formula
        if (orderData.items && orderData.items.length > 0) {
          // Calculate subtotal from items
          const subtotal = orderData.items.reduce((sum, item) => {
            return sum + (parseFloat(item.price || 0) * parseFloat(item.quantity || 0));
          }, 0);
          
          orderData.subtotal = subtotal;
          // Use delivery price from database
          orderData.delivery_fee = parseFloat(orderData.delivery || orderData.delivery_fee || 5000);
          // Calculate total
          orderData.total_amount = subtotal + orderData.delivery_fee - orderData.discount_amount;
        } else {
          // Fallback if no items
          orderData.subtotal = orderData.subtotal || 0;
          orderData.delivery_fee = orderData.delivery || orderData.delivery_fee || 5000;
          orderData.total_amount = orderData.total_price || orderData.total || orderData.total_amount || 0;
        }
        
        console.log('Order details loaded:', orderData);
        console.log('Subtotal:', orderData.subtotal, 'Delivery:', orderData.delivery_fee, 'Total:', orderData.total_amount);
        setSelectedOrder(orderData);
        
        // Initialize admin notes
        setAdminNotesValue(orderData.admin_notes || '');
        setIsEditingAdminNotes(false);
        
        // Initialize prepared items - restore from storage if exists, otherwise start with empty set
        const savedPreparedItems = preparedItemsByOrder[orderData.id];
        setPreparedItems(savedPreparedItems ? new Set(savedPreparedItems) : new Set());
        
        setDetailsDialogOpen(true);
      } else {
        setError(data.message || 'فشل في تحميل تفاصيل الطلب');
      }
    } catch (error) {
      console.error('ViewOrderDetails error:', error);
      setError('حدث خطأ في الاتصال بالخادم');
    }
  };

  if (!adminAuthService.isAuthenticated()) {
    navigate('/admin/login');
    return null;
  }

  return (
    <ThemeProvider theme={rtlTheme}>
      <div dir="rtl" style={{ direction: 'rtl', textAlign: 'right' }}>
        <Box sx={{ flexGrow: 1 }}>
          {/* Navigation Bar */}
          <AppBar position="static" sx={{ backgroundColor: '#10b981' }}>
            <Toolbar>
              {!isPackagingAdmin && (
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={() => navigate('/admin/dashboard')}
                  sx={{ mr: 2 }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                واجهة المجهز - إدارة التعبئة والتغليف
              </Typography>
              <Button
                variant="contained"
                color="warning"
                startIcon={<InventoryIcon />}
                onClick={() => setMissingProductsDialogOpen(true)}
                sx={{ ml: 2 }}
              >
                المنتجات المفقودة ({missingProducts.length})
              </Button>
              <IconButton
                color="inherit"
                onClick={handleClearCacheAndRefresh}
                sx={{ 
                  ml: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
                title="تحديث ومسح الـذاكرة"
              >
                <RefreshIcon />
              </IconButton>
              <IconButton
                color="inherit"
                onClick={handleLogout}
                sx={{ 
                  ml: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
                title="تسجيل الخروج"
              >
                <LogoutIcon />
              </IconButton>
            </Toolbar>
          </AppBar>

          <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 1, sm: 2 } }}>
            {/* Statistics Cards */}
            <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
              <Grid item xs={6} sm={4} md={2}>
                <Card 
                  sx={{ cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
                  onClick={() => {
                    setStatusFilter('');
                    setTodayShippedFilter(false);
                    setShippedDateFilter('');
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    p: { xs: 1, sm: 2 },
                    '&:last-child': { pb: { xs: 1, sm: 2 } }
                  }}>
                    <OrdersIcon sx={{ 
                      fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                      color: '#6366f1', 
                      mb: { xs: 0.5, sm: 1 }
                    }} />
                    <Typography 
                      variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.8rem' }
                      }}
                    >
                      {stats.total}
                    </Typography>
                    <Typography 
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        lineHeight: 1.2
                      }}
                    >
                      إجمالي الطلبات
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card 
                  sx={{ cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
                  onClick={() => {
                    setStatusFilter('confirmed');
                    setTodayShippedFilter(false);
                    setShippedDateFilter('');
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    p: { xs: 1, sm: 2 },
                    '&:last-child': { pb: { xs: 1, sm: 2 } }
                  }}>
                    <CheckIcon sx={{ 
                      fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                      color: '#3b82f6', 
                      mb: { xs: 0.5, sm: 1 }
                    }} />
                    <Typography 
                      variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.8rem' }
                      }}
                    >
                      {stats.confirmed}
                    </Typography>
                    <Typography 
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        lineHeight: 1.2
                      }}
                    >
                      مؤكدة
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card 
                  sx={{ 
                    backgroundColor: '#f0fdf4', 
                    border: '2px solid #10b981',
                    cursor: 'pointer', 
                    transition: 'transform 0.2s', 
                    '&:hover': { transform: 'scale(1.05)' } 
                  }}
                  onClick={() => {
                    setStatusFilter('shipped');
                    setTodayShippedFilter(false);
                    setShippedDateFilter('');
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    p: { xs: 1, sm: 2 },
                    '&:last-child': { pb: { xs: 1, sm: 2 } }
                  }}>
                    <ShippingIcon sx={{ 
                      fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                      color: '#10b981', 
                      mb: { xs: 0.5, sm: 1 }
                    }} />
                    <Typography 
                      variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.8rem' },
                        color: '#10b981'
                      }}
                    >
                      {stats.shipped}
                    </Typography>
                    <Typography 
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        lineHeight: 1.2,
                        color: '#065f46',
                        fontWeight: 600
                      }}
                    >
                      تم الشحن
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card 
                  sx={{ cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
                  onClick={() => {
                    setStatusFilter('delivered');
                    setTodayShippedFilter(false);
                    setShippedDateFilter('');
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    p: { xs: 1, sm: 2 },
                    '&:last-child': { pb: { xs: 1, sm: 2 } }
                  }}>
                    <CheckIcon sx={{ 
                      fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                      color: '#10b981', 
                      mb: { xs: 0.5, sm: 1 }
                    }} />
                    <Typography 
                      variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.8rem' }
                      }}
                    >
                      {stats.delivered}
                    </Typography>
                    <Typography 
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        lineHeight: 1.2
                      }}
                    >
                      تم التوصيل
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card 
                  sx={{ cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
                  onClick={() => {
                    setStatusFilter('pending');
                    setTodayShippedFilter(false);
                    setShippedDateFilter('');
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    p: { xs: 1, sm: 2 },
                    '&:last-child': { pb: { xs: 1, sm: 2 } }
                  }}>
                    <PendingIcon sx={{ 
                      fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                      color: '#f59e0b', 
                      mb: { xs: 0.5, sm: 1 }
                    }} />
                    <Typography 
                      variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.8rem' }
                      }}
                    >
                      {stats.pending}
                    </Typography>
                    <Typography 
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        lineHeight: 1.2
                      }}
                    >
                      في الانتظار
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card 
                  sx={{ 
                    backgroundColor: '#fef3c7', 
                    border: '2px solid #f59e0b',
                    cursor: 'pointer', 
                    transition: 'transform 0.2s', 
                    '&:hover': { transform: 'scale(1.05)' } 
                  }}
                  onClick={() => {
                    setStatusFilter('');
                    setTodayShippedFilter(true);
                    setShippedDateFilter('');
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    p: { xs: 1, sm: 2 },
                    '&:last-child': { pb: { xs: 1, sm: 2 } }
                  }}>
                    <ShippingIcon sx={{ 
                      fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                      color: '#f59e0b', 
                      mb: { xs: 0.5, sm: 1 }
                    }} />
                    <Typography 
                      variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.8rem' },
                        color: '#f59e0b'
                      }}
                    >
                      {stats.todayShipped}
                    </Typography>
                    <Typography 
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        lineHeight: 1.2,
                        color: '#92400e',
                        fontWeight: 600
                      }}
                    >
                      شحن اليوم
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card 
                  sx={{ cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
                  onClick={() => {
                    setStatusFilter('cancelled');
                    setTodayShippedFilter(false);
                    setShippedDateFilter('');
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    p: { xs: 1, sm: 2 },
                    '&:last-child': { pb: { xs: 1, sm: 2 } }
                  }}>
                    <CancelIcon sx={{ 
                      fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                      color: '#ef4444', 
                      mb: { xs: 0.5, sm: 1 }
                    }} />
                    <Typography 
                      variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.8rem' }
                      }}
                    >
                      {stats.cancelled}
                    </Typography>
                    <Typography 
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        lineHeight: 1.2
                      }}
                    >
                      ملغية
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Error/Success Messages */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            {/* Search and Filters */}
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12} sm={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="البحث في الطلبات (رقم الطلب، اسم العميل، رقم الهاتف)..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>تصفية حسب الحالة</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      label="تصفية حسب الحالة"
                    >
                      <MenuItem value="">كل الحالات</MenuItem>
                      {statusOptions.map((status) => (
                        <MenuItem key={status.value} value={status.value}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="تاريخ الشحن"
                    value={shippedDateFilter}
                    onChange={(e) => setShippedDateFilter(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText="عرض الطلبات المشحونة في تاريخ محدد"
                    sx={{
                      '& .MuiInputBase-input': {
                        textAlign: 'left'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      setSearchQuery('');
                      setStatusFilter('');
                      setTodayShippedFilter(false);
                      setShippedDateFilter('');
                    }}
                    sx={{ height: isMobile ? '40px' : '56px' }}
                  >
                    إزالة التصفية
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={generateDeliveryCSV}
                    disabled={selectedOrdersForExport.length === 0}
                    sx={{ 
                      height: isMobile ? '40px' : '56px',
                      backgroundColor: '#10b981',
                      '&:hover': {
                        backgroundColor: '#059669'
                      }
                    }}
                  >
                    تصدير توصيل ({selectedOrdersForExport.length})
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={generateStockExport}
                    disabled={selectedOrdersForExport.length === 0}
                    sx={{ 
                      height: isMobile ? '40px' : '56px',
                      backgroundColor: '#6366f1',
                      '&:hover': {
                        backgroundColor: '#4f46e5'
                      }
                    }}
                  >
                    تصدير مخزون ({selectedOrdersForExport.length})
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={generateProductsListExport}
                    disabled={selectedOrdersForExport.length === 0}
                    sx={{ 
                      height: isMobile ? '40px' : '56px',
                      backgroundColor: '#f59e0b',
                      '&:hover': {
                        backgroundColor: '#d97706'
                      }
                    }}
                  >
                    تصدير قائمة منتجات ({selectedOrdersForExport.length})
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={generatePrimeDeliveryExport}
                    disabled={selectedOrdersForExport.length === 0}
                    sx={{ 
                      height: isMobile ? '40px' : '56px',
                      backgroundColor: '#ec4899',
                      '&:hover': {
                        backgroundColor: '#db2777'
                      }
                    }}
                  >
                    تصدير Prime Delivery ({selectedOrdersForExport.length})
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Orders Table */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
              {isMobile ? (
                /* Mobile Card View */
                <Box sx={{ p: 2 }}>
                  {/* Select All Checkbox for Mobile */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 2,
                    pb: 2,
                    borderBottom: '2px solid #e5e7eb'
                  }}>
                    <Checkbox
                      color="primary"
                      checked={selectAllOrders}
                      onChange={handleSelectAllOrdersForExport}
                      sx={{ 
                        color: '#6b7280',
                        '&.Mui-checked': { color: '#10b981' }
                      }}
                    />
                    <Typography sx={{ fontWeight: 600, color: '#374151' }}>
                      تحديد الكل ({selectedOrdersForExport.length} محدد)
                    </Typography>
                  </Box>
                  
                  {loading ? (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      gap: 2,
                      py: 4
                    }}>
                      <Box sx={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        border: '4px solid #e0e7ff',
                        borderTop: '4px solid #4169E1',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' }
                        }
                      }} />
                      <Typography>جاري تحميل الطلبات...</Typography>
                    </Box>
                  ) : filteredOrders.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <OrdersIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                      <Typography>لا توجد طلبات</Typography>
                    </Box>
                  ) : (
                    <Stack spacing={2}>
                      {filteredOrders
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((order) => (
                          <Card 
                            key={order.id} 
                            sx={{ 
                              border: order.status === 'shipped' ? '2px solid #10b981' : '1px solid #e0e0e0',
                              backgroundColor: order.status === 'shipped' ? '#f0fdf4' : 'white',
                              borderRadius: 2
                            }}
                          >
                            <CardContent sx={{ p: 2 }}>
                              {/* Order Header */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                  <Checkbox
                                    color="primary"
                                    checked={selectedOrdersForExport.includes(order.id)}
                                    onChange={() => handleSelectOrderForExport(order.id)}
                                    sx={{ 
                                      color: '#6b7280',
                                      '&.Mui-checked': { color: '#10b981' },
                                      p: 0,
                                      mt: 0.5
                                    }}
                                  />
                                  <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                      طلب #{order.id}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {new Date(order.created_at).toLocaleDateString('ar-EG')}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Chip
                                  {...getStatusInfo(order.status)}
                                  label={getStatusInfo(order.status).label}
                                  size="small"
                                  sx={{
                                    backgroundColor: order.status === 'shipped' ? '#10b981' : undefined,
                                    color: order.status === 'shipped' ? 'white' : undefined,
                                    fontWeight: order.status === 'shipped' ? 'bold' : undefined
                                  }}
                                />
                              </Box>
                              
                              {/* Customer Info */}
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                  {order.customer_name || 'غير محدد'}
                                </Typography>
                                {order.customer_phone && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                      {order.customer_phone}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        navigator.clipboard.writeText(order.customer_phone);
                                        setSuccess('تم نسخ رقم الهاتف');
                                        setTimeout(() => setSuccess(''), 2000);
                                      }}
                                      sx={{ 
                                        color: '#6366f1', 
                                        backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                        '&:hover': { 
                                          backgroundColor: 'rgba(99, 102, 241, 0.2)' 
                                        },
                                        p: 0.5
                                      }}
                                    >
                                      <ContentCopyIcon sx={{ fontSize: '14px' }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleWhatsAppClick(order.customer_phone)}
                                      sx={{ 
                                        color: '#25D366', 
                                        backgroundColor: 'rgba(37, 211, 102, 0.1)',
                                        '&:hover': { 
                                          backgroundColor: 'rgba(37, 211, 102, 0.2)' 
                                        },
                                        p: 0.5
                                      }}
                                    >
                                      <PhoneIcon sx={{ fontSize: '16px' }} />
                                    </IconButton>
                                  </Box>
                                )}
                              </Box>
                              
                              {/* Order Items List */}
                              {order.items && order.items.length > 0 && (
                                <Box sx={{ 
                                  mb: 2, 
                                  p: 1.5, 
                                  backgroundColor: '#f8fafc',
                                  borderRadius: '8px',
                                  border: '1px solid #e2e8f0'
                                }}>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      fontWeight: 'bold', 
                                      color: '#475569',
                                      display: 'block',
                                      mb: 1
                                    }}
                                  >
                                    المنتجات:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {order.items.map((item, index) => (
                                      <Typography 
                                        key={index}
                                        variant="body2" 
                                        sx={{ 
                                          fontSize: '0.8rem',
                                          color: '#334155',
                                          lineHeight: 1.4
                                        }}
                                      >
                                        • {item.product_name || item.name || 'غير محدد'}
                                        {item.brand && (
                                          <Typography 
                                            component="span" 
                                            sx={{ 
                                              color: '#64748b',
                                              fontSize: '0.75rem',
                                              ml: 0.5
                                            }}
                                          >
                                            ({item.brand})
                                          </Typography>
                                        )}
                                        {item.quantity && (
                                          <Typography 
                                            component="span" 
                                            sx={{ 
                                              color: '#64748b',
                                              fontSize: '0.75rem',
                                              ml: 0.5
                                            }}
                                          >
                                            - الكمية: {item.quantity}
                                          </Typography>
                                        )}
                                      </Typography>
                                    ))}
                                  </Box>
                                </Box>
                              )}
                              
                              {/* Status Change */}
                              <Box>
                                <Typography variant="caption" sx={{ color: '#6b7280', mb: 0.5, display: 'block' }}>
                                  تغيير الحالة:
                                </Typography>
                                <FormControl size="small" fullWidth>
                                  <Select
                                    value={order.status}
                                    onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                    sx={{ 
                                      fontSize: '0.875rem',
                                      borderRadius: '8px',
                                      backgroundColor: '#f8fafc',
                                      border: '1px solid #e2e8f0',
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none'
                                      }
                                    }}
                                  >
                                    {statusOptions.map((status) => (
                                      <MenuItem key={status.value} value={status.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {React.cloneElement(status.icon, { 
                                            sx: { fontSize: '16px', color: getStatusColor(status.color) } 
                                          })}
                                          <Typography sx={{ fontSize: '0.875rem' }}>
                                            {status.label}
                                          </Typography>
                                        </Box>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                              
                              {/* View Details Button */}
                              <Box sx={{ mt: 2 }}>
                                <Button
                                  variant="outlined"
                                  fullWidth
                                  startIcon={<ViewIcon />}
                                  onClick={() => handleViewOrderDetails(order)}
                                  sx={{
                                    borderColor: '#4169E1',
                                    color: '#4169E1',
                                    '&:hover': {
                                      borderColor: '#2952cc',
                                      backgroundColor: 'rgba(65, 105, 225, 0.05)'
                                    }
                                  }}
                                >
                                  عرض التفاصيل
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                    </Stack>
                  )}
                </Box>
              ) : (
                /* Desktop Table View */
                <TableContainer 
                  sx={{
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    border: '1px solid #e0e7ff',
                    overflow: 'hidden',
                    backgroundColor: 'white'
                  }}
                >
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ fontWeight: 700 }}>
                          <Checkbox
                            color="primary"
                            checked={selectAllOrders}
                            onChange={handleSelectAllOrdersForExport}
                            sx={{ 
                              color: '#6b7280',
                              '&.Mui-checked': { color: '#10b981' }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                          رقم الطلب
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                          العميل
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                          رقم الهاتف
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                          الحالة
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                          تاريخ الطلب
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                          الإجراءات
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell 
                            colSpan={7} 
                            align="center"
                            sx={{
                              padding: '60px 20px',
                              backgroundColor: '#fafbfc'
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center',
                              gap: 2
                            }}>
                              <Box sx={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                border: '4px solid #e0e7ff',
                                borderTop: '4px solid #4169E1',
                                animation: 'spin 1s linear infinite',
                                '@keyframes spin': {
                                  '0%': { transform: 'rotate(0deg)' },
                                  '100%': { transform: 'rotate(360deg)' }
                                }
                              }} />
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  color: '#6b7280',
                                  fontSize: '1rem',
                                  fontWeight: 500
                                }}
                              >
                                جاري تحميل الطلبات...
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : filteredOrders.length === 0 ? (
                        <TableRow>
                          <TableCell 
                            colSpan={7} 
                            align="center"
                            sx={{
                              padding: '60px 20px',
                              backgroundColor: '#fafbfc'
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center',
                              gap: 2
                            }}>
                              <Box sx={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                                backgroundColor: '#f3f4f6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: 1
                              }}>
                                <OrdersIcon sx={{ fontSize: '30px', color: '#9ca3af' }} />
                              </Box>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  color: '#374151',
                                  fontSize: '1.1rem',
                                  fontWeight: 600,
                                  marginBottom: 0.5
                                }}
                              >
                                لا توجد طلبات
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#6b7280',
                                  fontSize: '0.9rem',
                                  textAlign: 'center'
                                }}
                              >
                                لم يتم العثور على أي طلبات تطابق معايير البحث الحالية
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredOrders
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((order) => (
                            <TableRow 
                              key={order.id}
                              sx={{
                                backgroundColor: order.status === 'shipped' ? '#f0fdf4' : 'white',
                                borderLeft: order.status === 'shipped' ? '4px solid #10b981' : 'none',
                                '&:hover': {
                                  backgroundColor: order.status === 'shipped' ? '#dcfce7' : '#f8fafc',
                                  transition: 'all 0.2s ease'
                                }
                              }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  color="primary"
                                  checked={selectedOrdersForExport.includes(order.id)}
                                  onChange={() => handleSelectOrderForExport(order.id)}
                                  sx={{ 
                                    color: '#6b7280',
                                    '&.Mui-checked': { color: '#10b981' }
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                                <Typography 
                                  variant="subtitle1" 
                                  sx={{ 
                                    fontWeight: 600,
                                    color: order.status === 'shipped' ? '#065f46' : '#4169E1',
                                    fontSize: '0.95rem'
                                  }}
                                >
                                  #{order.id}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                                <Typography 
                                  variant="subtitle2" 
                                  sx={{ 
                                    fontWeight: 600, 
                                    color: '#111827',
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {order.customer_name || 'غير محدد'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                                {order.customer_phone ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                      {order.customer_phone}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        navigator.clipboard.writeText(order.customer_phone);
                                        setSuccess('تم نسخ رقم الهاتف');
                                        setTimeout(() => setSuccess(''), 2000);
                                      }}
                                      sx={{ 
                                        color: '#6366f1',
                                        backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                        '&:hover': {
                                          backgroundColor: 'rgba(99, 102, 241, 0.2)',
                                          transform: 'scale(1.05)'
                                        },
                                        transition: 'all 0.2s ease',
                                        borderRadius: '50%',
                                        padding: '4px'
                                      }}
                                    >
                                      <ContentCopyIcon sx={{ fontSize: '14px' }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleWhatsAppClick(order.customer_phone)}
                                      sx={{ 
                                        color: '#25D366',
                                        backgroundColor: 'rgba(37, 211, 102, 0.1)',
                                        '&:hover': {
                                          backgroundColor: 'rgba(37, 211, 102, 0.2)',
                                          transform: 'scale(1.05)'
                                        },
                                        transition: 'all 0.2s ease',
                                        borderRadius: '50%',
                                        padding: '4px'
                                      }}
                                    >
                                      <PhoneIcon sx={{ fontSize: '16px' }} />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    غير محدد
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                  <Select
                                    value={order.status}
                                    onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                    displayEmpty
                                    sx={{ 
                                      fontSize: '0.875rem',
                                      borderRadius: '8px',
                                      backgroundColor: '#f8fafc',
                                      border: '1px solid #e2e8f0',
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none'
                                      },
                                      '&:hover': {
                                        backgroundColor: '#f1f5f9',
                                        border: '1px solid #cbd5e1'
                                      },
                                      '&.Mui-focused': {
                                        backgroundColor: 'white',
                                        boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.1)',
                                        border: '1px solid #10b981'
                                      },
                                      '& .MuiSelect-select': {
                                        paddingY: '8px',
                                        paddingX: '12px',
                                        display: 'flex',
                                        alignItems: 'center'
                                      }
                                    }}
                                  >
                                    {statusOptions.map((status) => (
                                      <MenuItem 
                                        key={status.value} 
                                        value={status.value}
                                        sx={{
                                          '&:hover': {
                                            backgroundColor: '#f8fafc'
                                          }
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {React.cloneElement(status.icon, { 
                                            sx: { fontSize: '16px', color: getStatusColor(status.color) } 
                                          })}
                                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                            {status.label}
                                          </Typography>
                                        </Box>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: '#6b7280',
                                    fontSize: '0.875rem',
                                    fontWeight: 500
                                  }}
                                >
                                  {new Date(order.created_at).toLocaleDateString('ar-EG')}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewOrderDetails(order)}
                                  sx={{ 
                                    color: '#4169E1',
                                    backgroundColor: 'rgba(65, 105, 225, 0.1)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(65, 105, 225, 0.2)',
                                      transform: 'scale(1.05)'
                                    },
                                    transition: 'all 0.2s ease',
                                    borderRadius: '8px',
                                    padding: '6px'
                                  }}
                                  title="عرض التفاصيل"
                                >
                                  <ViewIcon sx={{ fontSize: '18px' }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              <TablePagination
                component="div"
                count={filteredOrders.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={isMobile ? "الصفوف:" : "عدد الصفوف في الصفحة:"}
                labelDisplayedRows={({ from, to, count }) => 
                  isMobile ? `${from}-${to}/${count}` : `${from}-${to} من أصل ${count !== -1 ? count : `أكثر من ${to}`}`
                }
                sx={{
                  backgroundColor: '#f8fafc',
                  borderTop: '1px solid #e2e8f0',
                  borderRadius: '0 0 12px 12px',
                  '& .MuiTablePagination-toolbar': {
                    px: { xs: 2, sm: 3 },
                    minHeight: { xs: 52, sm: 56 },
                    backgroundColor: '#f8fafc'
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    fontWeight: 500,
                    color: '#374151'
                  },
                  '& .MuiTablePagination-select': {
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    fontSize: '0.875rem'
                  },
                  '& .MuiTablePagination-actions': {
                    '& .MuiIconButton-root': {
                      backgroundColor: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      margin: '0 2px',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                        borderColor: '#9ca3af'
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#f9fafb',
                        borderColor: '#e5e7eb',
                        color: '#d1d5db'
                      }
                    }
                  }
                }}
              />
            </Paper>
          </Container>
        </Box>

        {/* Order Details Dialog */}
        <Dialog 
          open={detailsDialogOpen} 
          onClose={() => {
            setDetailsDialogOpen(false);
            setEditingItems(false);
          }} 
          maxWidth="lg" 
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: { 
              height: isMobile ? '100vh' : 'auto',
              maxHeight: isMobile ? '100vh' : '90vh',
              display: 'flex',
              flexDirection: 'column'
            }
          }}
        >
          <DialogTitle sx={{ pb: 1, px: { xs: 2, sm: 3 } }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              gap: 1
            }}>
              <Typography 
                variant={{ xs: 'h6', sm: 'h5' }} 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}
              >
                تفاصيل الطلب #{selectedOrder?.id}
              </Typography>
              {selectedOrder && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    select
                    size="small"
                    value={selectedOrder.status}
                    onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value)}
                    sx={{ minWidth: 150 }}
                    SelectProps={{
                      native: true
                    }}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </TextField>
                  <Chip
                    icon={getStatusInfo(selectedOrder.status).icon}
                    label={getStatusInfo(selectedOrder.status).label}
                    color={getStatusInfo(selectedOrder.status).color}
                    size={isMobile ? "medium" : "large"}
                    sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.9rem' }, 
                      px: { xs: 1, sm: 2 }
                    }}
                  />
                </Box>
              )}
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ 
            p: { xs: 2, sm: 3, md: 4 },
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden'
          }}>
            {selectedOrder && (
              <Box>
                {/* Essential Order Information */}
                <Paper sx={{ p: { xs: 2, sm: 3 }, backgroundColor: '#f8f9fa' }}>
                  <Typography 
                    variant={{ xs: 'h6', sm: 'h6' }} 
                    sx={{ 
                      mb: 3, 
                      color: '#10b981', 
                      fontWeight: 'bold',
                      fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                  >
                    📋 معلومات الطلب
                  </Typography>
                  
                  <Box sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    backgroundColor: 'white', 
                    borderRadius: 2, 
                    border: '2px solid #10b981' 
                  }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            رقم الطلب
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10b981', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            #{selectedOrder.order_code || selectedOrder.id}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={6} sm={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            اسم العميل
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {selectedOrder.customer_name || 'غير محدد'}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={6} sm={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            رقم الهاتف
                          </Typography>
                          {selectedOrder.customer_phone ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1.1rem' } }}>
                                {selectedOrder.customer_phone}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    navigator.clipboard.writeText(selectedOrder.customer_phone);
                                    setSuccess('تم نسخ رقم الهاتف');
                                    setTimeout(() => setSuccess(''), 2000);
                                  }}
                                  sx={{ 
                                    color: '#6366f1', 
                                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                    '&:hover': { 
                                      backgroundColor: 'rgba(99, 102, 241, 0.2)',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease',
                                    p: { xs: 0.5, sm: 1 }
                                  }}
                                  title="نسخ رقم الهاتف"
                                >
                                  <ContentCopyIcon sx={{ fontSize: { xs: '14px', sm: '18px' } }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleWhatsAppClick(selectedOrder.customer_phone)}
                                  sx={{ 
                                    color: '#25D366', 
                                    backgroundColor: 'rgba(37, 211, 102, 0.1)',
                                    '&:hover': { 
                                      backgroundColor: 'rgba(37, 211, 102, 0.2)',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease',
                                    p: { xs: 0.5, sm: 1 }
                                  }}
                                  title="إرسال رسالة واتساب"
                                >
                                  <PhoneIcon sx={{ fontSize: { xs: '14px', sm: '18px' } }} />
                                </IconButton>
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body1" color="text.secondary">
                              غير محدد
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      
                      <Grid item xs={6} sm={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            المدينة
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1.1rem' } }}>
                            {selectedOrder.city || 'غير محدد'}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            العنوان الكامل
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1.1rem' }, lineHeight: 1.6 }}>
                            {selectedOrder.shipping_address || 'غير محدد'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>

                {/* Admin Notes Section */}
                <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3, backgroundColor: '#f8f9fa' }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 1.5,
                    flexWrap: 'wrap',
                    gap: 1
                  }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#4169E1',
                        fontSize: { xs: '0.95rem', sm: '1.1rem' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <span>📝</span> ملاحظات الإدارة
                    </Typography>
                    {!isEditingAdminNotes ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setIsEditingAdminNotes(true)}
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          py: { xs: 0.5, sm: 0.75 },
                          px: { xs: 1, sm: 2 },
                          borderColor: '#4169E1',
                          color: '#4169E1',
                          '&:hover': {
                            borderColor: '#2952cc',
                            backgroundColor: 'rgba(65, 105, 225, 0.05)'
                          }
                        }}
                      >
                        تعديل
                      </Button>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setIsEditingAdminNotes(false);
                            setAdminNotesValue(selectedOrder.admin_notes || '');
                          }}
                          disabled={savingAdminNotes}
                          sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            py: { xs: 0.5, sm: 0.75 },
                            borderColor: '#6b7280',
                            color: '#6b7280'
                          }}
                        >
                          إلغاء
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleSaveAdminNotes}
                          disabled={savingAdminNotes}
                          sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            py: { xs: 0.5, sm: 0.75 },
                            backgroundColor: '#4169E1',
                            '&:hover': {
                              backgroundColor: '#2952cc'
                            }
                          }}
                        >
                          {savingAdminNotes ? 'جاري الحفظ...' : 'حفظ'}
                        </Button>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ 
                    p: { xs: 1.5, sm: 2 }, 
                    backgroundColor: isEditingAdminNotes ? '#f0f7ff' : '#fff9e6', 
                    borderRadius: 1, 
                    border: isEditingAdminNotes ? '2px solid #4169E1' : '1px solid #ffd54f',
                    minHeight: 80
                  }}>
                    {isEditingAdminNotes ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={adminNotesValue}
                        onChange={(e) => setAdminNotesValue(e.target.value)}
                        placeholder="أضف ملاحظات داخلية للإدارة فقط..."
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            fontSize: { xs: '0.85rem', sm: '1rem' }
                          }
                        }}
                      />
                    ) : (
                      <Typography sx={{ 
                        whiteSpace: 'pre-wrap',
                        fontSize: { xs: '0.85rem', sm: '1rem' },
                        color: adminNotesValue ? 'inherit' : '#999',
                        fontStyle: adminNotesValue ? 'normal' : 'italic',
                        lineHeight: 1.6
                      }}>
                        {adminNotesValue || 'لا توجد ملاحظات إدارية'}
                      </Typography>
                    )}
                  </Box>
                </Paper>

                {/* Products Table */}
                {selectedOrder.items && selectedOrder.items.length > 0 && (
                  <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3, backgroundColor: '#f8f9fa' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography 
                        variant={{ xs: 'h6', sm: 'h6' }} 
                        sx={{ 
                          color: '#10b981', 
                          fontWeight: 'bold',
                          fontSize: { xs: '1.1rem', sm: '1.25rem' }
                        }}
                      >
                        🛍️ المنتجات المطلوبة
                      </Typography>
                      {editingItems && (
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleOpenAddProductDialog}
                          sx={{
                            backgroundColor: '#10b981',
                            '&:hover': {
                              backgroundColor: '#059669'
                            },
                            fontSize: { xs: '0.8rem', sm: '0.9rem' },
                            px: { xs: 1.5, sm: 2 }
                          }}
                        >
                          إضافة منتج
                        </Button>
                      )}
                    </Box>
                    
                    <TableContainer 
                      sx={{ 
                        backgroundColor: 'white',
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        overflow: 'auto',
                        overflowX: 'auto'
                      }}
                    >
                      <Table>
                        <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', width: '60px', textAlign: 'center' }}>
                              تم التجهيز
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', width: '80px' }}>
                              الصورة
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                              اسم المنتج
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', textAlign: 'center', width: editingItems ? '150px' : '100px' }}>
                              الكمية
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', textAlign: 'center', width: { xs: '70px', sm: '80px' } }}>
                              إجراءات
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedOrder.items.map((item, index) => {
                            const isPrepared = preparedItems.has(item.id);
                            return (
                            <TableRow 
                              key={item.id || index}
                              sx={{
                                backgroundColor: isPrepared ? '#d4edda' : '#f8d7da',
                                '&:hover': {
                                  backgroundColor: isPrepared ? '#c3e6cb' : '#f5c6cb'
                                },
                                transition: 'background-color 0.3s ease'
                              }}
                            >
                              <TableCell sx={{ padding: '12px', textAlign: 'center' }}>
                                <Checkbox
                                  checked={isPrepared}
                                  onChange={() => handleToggleItemPrepared(item.id)}
                                  sx={{
                                    color: isPrepared ? '#10b981' : '#6b7280',
                                    transform: 'scale(1.2)',
                                    '&.Mui-checked': {
                                      color: '#10b981',
                                      animation: 'checkPulse 0.3s ease-in-out',
                                    },
                                    '&:hover': {
                                      backgroundColor: isPrepared ? 'rgba(16, 185, 129, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                                    },
                                    '@keyframes checkPulse': {
                                      '0%': {
                                        transform: 'scale(1.2)',
                                      },
                                      '50%': {
                                        transform: 'scale(1.4)',
                                      },
                                      '100%': {
                                        transform: 'scale(1.2)',
                                      },
                                    },
                                  }}
                                  icon={
                                    <Box
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: 1,
                                        border: '2px solid #6b7280',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#f3f4f6',
                                      }}
                                    />
                                  }
                                  checkedIcon={
                                    <Box
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: 1,
                                        border: '2px solid #10b981',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#10b981',
                                        color: 'white',
                                      }}
                                    >
                                      <CheckIcon sx={{ fontSize: 18 }} />
                                    </Box>
                                  }
                                />
                              </TableCell>
                              <TableCell sx={{ padding: '12px' }}>
                                {item.product_image ? (
                                  <>
                                    <Box
                                      component="img"
                                      src={item.product_image.startsWith('http') ? 
                                        item.product_image : 
                                        `${API_BASE_URL}/api/uploads/products/${item.product_image}`
                                      }
                                      alt={item.product_name}
                                      sx={{ 
                                        width: 60, 
                                        height: 60, 
                                        borderRadius: 1, 
                                        objectFit: 'cover',
                                        border: '1px solid #e0e0e0',
                                        backgroundColor: '#f9f9f9'
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                    <Box 
                                      sx={{ 
                                        width: 60, 
                                        height: 60, 
                                        borderRadius: 1, 
                                        border: '1px dashed #ccc',
                                        display: 'none',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#f9f9f9'
                                      }}
                                    >
                                      <ImageIcon sx={{ fontSize: 24, color: '#ccc' }} />
                                    </Box>
                                  </>
                                ) : (
                                  <Box 
                                    sx={{ 
                                      width: 60, 
                                      height: 60, 
                                      borderRadius: 1, 
                                      border: '1px dashed #ccc',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: '#f9f9f9'
                                    }}
                                  >
                                    <ImageIcon sx={{ fontSize: 24, color: '#ccc' }} />
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell sx={{ padding: '12px' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  {item.product_name}
                                </Typography>
                                {(item.product_sku || item.brand_name) && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    {item.product_sku && `كود: ${item.product_sku}`}
                                    {item.product_sku && item.brand_name && ' | '}
                                    {item.brand_name && `البراند: ${item.brand_name}`}
                                  </Typography>
                                )}
                                {item.variant_info && (
                                  <Typography variant="caption" color="text.secondary">
                                    {item.variant_info}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ padding: '12px', textAlign: 'center' }}>
                                {editingItems ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleQuantityChange(item.id, -1)}
                                      disabled={item.quantity <= 1}
                                      sx={{ 
                                        width: { xs: 28, sm: 32 },
                                        height: { xs: 28, sm: 32 },
                                        backgroundColor: '#fee2e2',
                                        color: '#dc2626',
                                        '&:hover': { 
                                          backgroundColor: '#fecaca',
                                          transform: 'scale(1.1)'
                                        },
                                        '&.Mui-disabled': { 
                                          backgroundColor: '#f9fafb',
                                          color: '#d1d5db'
                                        },
                                        transition: 'all 0.2s'
                                      }}
                                    >
                                      <RemoveIcon sx={{ fontSize: { xs: '14px', sm: '16px' } }} />
                                    </IconButton>
                                    <Typography sx={{ 
                                      minWidth: { xs: '25px', sm: '35px' }, 
                                      textAlign: 'center', 
                                      fontWeight: 'bold',
                                      fontSize: { xs: '0.875rem', sm: '1rem' },
                                      backgroundColor: '#f3f4f6',
                                      borderRadius: 1,
                                      py: 0.5,
                                      px: 1
                                    }}>
                                      {item.quantity}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleQuantityChange(item.id, 1)}
                                      sx={{ 
                                        width: { xs: 28, sm: 32 },
                                        height: { xs: 28, sm: 32 },
                                        backgroundColor: '#dcfce7',
                                        color: '#16a34a',
                                        '&:hover': { 
                                          backgroundColor: '#bbf7d0',
                                          transform: 'scale(1.1)'
                                        },
                                        transition: 'all 0.2s'
                                      }}
                                    >
                                      <AddIcon sx={{ fontSize: { xs: '14px', sm: '16px' } }} />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Chip 
                                    label={item.quantity} 
                                    size="small"
                                    sx={{ 
                                      backgroundColor: '#10b981',
                                      color: 'white',
                                      fontWeight: 'bold',
                                      minWidth: '40px',
                                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                    }}
                                  />
                                )}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center', padding: { xs: '6px', sm: '8px' } }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                                  {editingItems && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteItem(item.id)}
                                      sx={{ 
                                        width: { xs: 32, sm: 36 },
                                        height: { xs: 32, sm: 36 },
                                        color: 'white',
                                        backgroundColor: '#ef4444',
                                        '&:hover': { 
                                          backgroundColor: '#dc2626',
                                          transform: 'scale(1.1)'
                                        },
                                        transition: 'all 0.2s',
                                        boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)'
                                      }}
                                      title="حذف المنتج"
                                    >
                                      <DeleteIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />
                                    </IconButton>
                                  )}
                                  <IconButton
                                    size="small"
                                    onClick={() => handleMarkProductAsMissing(item, selectedOrder.id)}
                                    sx={{ 
                                      width: { xs: 32, sm: 36 },
                                      height: { xs: 32, sm: 36 },
                                      color: '#f59e0b',
                                      bgcolor: '#fef3c7',
                                      '&:hover': { 
                                        bgcolor: '#fde68a',
                                        transform: 'scale(1.1)'
                                      },
                                      transition: 'all 0.2s',
                                      boxShadow: '0 2px 4px rgba(245, 158, 11, 0.2)'
                                    }}
                                    title="إضافة إلى قائمة المفقودات"
                                  >
                                    <WarningIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}

                {/* Total Price Section */}
                <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3, backgroundColor: '#f8f9fa' }}>
                  <Box sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    backgroundColor: 'white', 
                    borderRadius: 2, 
                    border: '2px solid #10b981' 
                  }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#374151' }}>
                          المجموع الفرعي:
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'left' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#374151' }}>
                          {parseFloat(selectedOrder.subtotal || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} IQD
                        </Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#374151' }}>
                          رسوم التوصيل:
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'left' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#374151' }}>
                          {parseFloat(selectedOrder.delivery_fee || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} IQD
                        </Typography>
                      </Grid>

                      {selectedOrder.discount_amount > 0 && (
                        <>
                          <Grid item xs={6}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#ef4444' }}>
                              الخصم {selectedOrder.coupon_code && `(${selectedOrder.coupon_code})`}:
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ textAlign: 'left' }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#ef4444' }}>
                              -{parseFloat(selectedOrder.discount_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} IQD
                            </Typography>
                          </Grid>
                        </>
                      )}

                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                          المجموع الكلي:
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'left' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                          {(() => {
                            const subtotal = parseFloat(selectedOrder.subtotal || 0);
                            const delivery = parseFloat(selectedOrder.delivery_fee || 0);
                            const discount = parseFloat(selectedOrder.discount_amount || 0);
                            const finalTotal = subtotal + delivery - discount;
                            return finalTotal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                          })()} IQD
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Box>
            )}
          </DialogContent>
          
          <DialogActions sx={{ 
            p: { xs: 2, sm: 3 }, 
            backgroundColor: '#f8f9fa', 
            gap: 1.5,
            flexDirection: { xs: 'column', sm: 'row' },
            '& .MuiButton-root': {
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: '140px' }
            }
          }}>
            {editingItems ? (
              <>
                <Button 
                  onClick={() => setEditingItems(false)} 
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  sx={{
                    borderColor: '#6b7280',
                    color: '#6b7280',
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: '#4b5563',
                      backgroundColor: 'rgba(107, 114, 128, 0.1)',
                      borderWidth: 2
                    },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    py: { xs: 1.2, sm: 1 }
                  }}
                >
                  إلغاء
                </Button>
                <Button 
                  onClick={handleSaveOrderChanges} 
                  variant="contained"
                  startIcon={<CheckIcon />}
                  sx={{
                    backgroundColor: '#10b981',
                    '&:hover': {
                      backgroundColor: '#059669',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                    },
                    transition: 'all 0.2s',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    py: { xs: 1.2, sm: 1 },
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                  }}
                >
                  حفظ التغييرات
                </Button>
              </>
            ) : (
              <>
                <Button 
                  onClick={() => setEditingItems(true)} 
                  variant="outlined"
                  startIcon={<EditIcon />}
                  sx={{
                    borderColor: '#10b981',
                    color: '#10b981',
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: '#059669',
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      borderWidth: 2
                    },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    py: { xs: 1.2, sm: 1 }
                  }}
                >
                  تعديل المنتجات
                </Button>
                <Button 
                  onClick={() => {
                    setDetailsDialogOpen(false);
                    setEditingItems(false);
                  }} 
                  variant="contained"
                  startIcon={<CloseIcon />}
                  sx={{
                    backgroundColor: '#10b981',
                    '&:hover': {
                      backgroundColor: '#059669',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                    },
                    transition: 'all 0.2s',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    py: { xs: 1.2, sm: 1 },
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                  }}
                >
                  إغلاق
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Add Product Dialog */}
        <Dialog 
          open={addProductDialogOpen} 
          onClose={() => setAddProductDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            إضافة منتج إلى الطلب #{selectedOrder?.id}
          </DialogTitle>
          <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Product Search */}
            <TextField
              fullWidth
              variant="outlined"
              placeholder="البحث عن منتج (الاسم، الكود، البراند)..."
              value={productSearchQuery}
              onChange={(e) => setProductSearchQuery(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Products List */}
            {loadingProducts ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography>جاري البحث عن المنتجات...</Typography>
              </Box>
            ) : productSearchQuery.length < 2 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <SearchIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary">
                  اكتب على الأقل حرفين للبحث عن المنتجات
                </Typography>
              </Box>
            ) : availableProducts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">لا توجد منتجات مطابقة للبحث</Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 400, overflowY: 'auto', mb: 3 }}>
                {getFilteredProducts().map((product) => (
                  <Paper
                    key={product.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      cursor: 'pointer',
                      border: selectedProduct?.id === product.id ? '2px solid #10b981' : '1px solid #e0e0e0',
                      backgroundColor: selectedProduct?.id === product.id ? '#f0fdf4' : 'white',
                      '&:hover': { backgroundColor: '#f8f9fa' }
                    }}
                    onClick={() => setSelectedProduct(product)}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={3} sm={2}>
                        {(product.image_url || product.image) ? (
                          <Box
                            component="img"
                            src={(product.image_url || product.image).startsWith('http') ? 
                              (product.image_url || product.image) : 
                              `${API_BASE_URL}/api/uploads/products/${product.image_url || product.image}`
                            }
                            alt={product.name}
                            sx={{ 
                              width: 60, 
                              height: 60, 
                              borderRadius: 1,
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <Box sx={{ width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f3f4f6', borderRadius: 1 }}>
                            <ImageIcon sx={{ fontSize: 30, color: '#9ca3af' }} />
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs={5} sm={6}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                          {product.name}
                        </Typography>
                        {product.sku && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            كود: {product.sku}
                          </Typography>
                        )}
                        {product.brand_name && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            البراند: {product.brand_name}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={4} sm={4}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10b981', fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                          {parseFloat(product.after_discount || product.sell_price || product.price || 0).toLocaleString()} IQD
                        </Typography>
                        {product.after_discount && (product.sell_price || product.price) && parseFloat(product.after_discount) < parseFloat(product.sell_price || product.price) && (
                          <Typography variant="caption" sx={{ display: 'block', textDecoration: 'line-through', color: 'text.secondary' }}>
                            {parseFloat(product.sell_price || product.price).toLocaleString()} IQD
                          </Typography>
                        )}
                        <Chip
                          label={(product.active === 1 || product.active === '1') ? 'متوفر' : 'غير متوفر'}
                          color={(product.active === 1 || product.active === '1') ? 'success' : 'error'}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>
            )}

            {/* Quantity Input */}
            {selectedProduct && (
              <Box sx={{ mt: 3, p: 3, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                  المنتج المحدد: {selectedProduct.name}
                </Typography>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="الكمية"
                      value={newProductQuantity}
                      onChange={(e) => setNewProductQuantity(parseInt(e.target.value) || 1)}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1">
                      <strong>إجمالي السعر:</strong> {(newProductQuantity * parseFloat(selectedProduct.after_discount || selectedProduct.sell_price || selectedProduct.price || 0)).toLocaleString()} IQD
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
            <Button onClick={() => setAddProductDialogOpen(false)} variant="outlined">
              إلغاء
            </Button>
            <Button 
              onClick={handleAddProductToOrder} 
              variant="contained"
              disabled={!selectedProduct}
              sx={{
                backgroundColor: '#10b981',
                '&:hover': {
                  backgroundColor: '#059669'
                }
              }}
            >
              إضافة إلى الطلب
            </Button>
          </DialogActions>
        </Dialog>

        {/* Missing Products Dialog */}
        <Dialog
          open={missingProductsDialogOpen}
          onClose={() => setMissingProductsDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ pb: 1, px: { xs: 2, sm: 3 } }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <InventoryIcon sx={{ color: '#f59e0b', fontSize: 32 }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  المنتجات المفقودة
                </Typography>
                <Chip 
                  label={`${missingProducts.length} منتج`}
                  color="warning"
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {missingProducts.length > 0 && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      onClick={exportMissingProductsToExcel}
                    >
                      تصدير Excel
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleClearMissingProducts}
                    >
                      مسح الكل
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
            {missingProducts.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2
              }}>
                <InventoryIcon sx={{ fontSize: 64, color: '#d1d5db' }} />
                <Typography variant="h6" color="text.secondary">
                  لا توجد منتجات مفقودة
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  قم بإضافة المنتجات المفقودة من تفاصيل الطلبات
                </Typography>
              </Box>
            ) : (
              <TableContainer 
                sx={{ 
                  backgroundColor: 'white',
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                  overflow: 'hidden'
                }}
              >
                <Table>
                  <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', width: '80px' }}>
                        الصورة
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                        اسم المنتج
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                        SKU
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                        العلامة التجارية
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', textAlign: 'center' }}>
                        الكمية
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', textAlign: 'center' }}>
                        رقم الطلب
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', textAlign: 'center', width: '80px' }}>
                        إجراء
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {missingProducts.map((product) => (
                      <TableRow 
                        key={product.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#fef3c7'
                          }
                        }}
                      >
                        <TableCell sx={{ padding: '12px' }}>
                          {product.image ? (
                            <>
                              <Box
                                component="img"
                                src={product.image.startsWith('http') ? 
                                  product.image : 
                                  `${API_BASE_URL}/api/uploads/products/${product.image}`
                                }
                                alt={product.productName}
                                sx={{ 
                                  width: 60, 
                                  height: 60, 
                                  borderRadius: 1, 
                                  objectFit: 'cover',
                                  border: '1px solid #e0e0e0',
                                  backgroundColor: '#f9f9f9'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <Box 
                                sx={{ 
                                  width: 60, 
                                  height: 60, 
                                  borderRadius: 1, 
                                  border: '1px dashed #ccc',
                                  display: 'none',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: '#f9f9f9'
                                }}
                              >
                                <ImageIcon sx={{ fontSize: 24, color: '#ccc' }} />
                              </Box>
                            </>
                          ) : (
                            <Box 
                              sx={{ 
                                width: 60, 
                                height: 60, 
                                borderRadius: 1, 
                                border: '1px dashed #ccc',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f9f9f9'
                              }}
                            >
                              <ImageIcon sx={{ fontSize: 24, color: '#ccc' }} />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {product.productName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {product.sku || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {product.brandName || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Chip 
                            label={product.quantity} 
                            size="small"
                            color="warning"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Chip 
                            label={`#${product.orderId}`}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', padding: '12px' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveMissingProduct(product.id)}
                            sx={{ 
                              color: '#ef4444',
                              backgroundColor: 'rgba(239, 68, 68, 0.1)',
                              '&:hover': { 
                                backgroundColor: 'rgba(239, 68, 68, 0.2)' 
                              }
                            }}
                            title="إزالة من القائمة"
                          >
                            <DeleteIcon sx={{ fontSize: '18px' }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>

          <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
            <Button 
              onClick={() => setMissingProductsDialogOpen(false)} 
              variant="contained"
            >
              إغلاق
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  );
};

export default PackagingManagement;